<template>
  <div
    ref="appDiv"
    :style="`display: grid; grid-template-rows: ${
      isMonitorViewEnabled ? '0' : '3.5em'
    } minmax(0, auto) 0`"
    class="h-100 overflow-y-hidden"
    :class="{
      'fake-dark-selector': isDarkModeEnabled,
    }"
  >
    <div class="header">
      <HeadNavbar
        v-show="!isMonitorViewEnabled"
        v-model:show-sidebar="showSidebar"
        @open-global-search="showNewGlobalSearch = true"
      />
    </div>
    <div class="main h-100">
      <NewSidebarComponent
        v-if="!isMonitorViewEnabled && showSidebar && useNewSidebar"
        class="doNotPrintMe"
        @open-command-menu="showLegacyCommandMenu = true"
      />
      <SidebarComponent
        v-else-if="!isMonitorViewEnabled && showSidebar"
        class="doNotPrintMe"
        @open-command-menu="showLegacyCommandMenu = true"
      />
      <LISAThemeProvider class="rightContainer">
        <router-view
          ref="mainRouterView"
          class="router-view"
          :class="{
            'background-image': useBackgroundImage,
            isEmma: ENV.VITE_APP_BRAND_NAME === 'EMMA' && useBackgroundImage,
          }"
        />

        <KeyCloakUserDialog />
      </LISAThemeProvider>
    </div>

    <Placeholder />
    <RFIDScanComponent ref="rfidComponent" />

    <LISAButton
      v-tooltip.top="t('sentry.formTitle')"
      class="p-1 m-0 SentryFeedbackButton doNotPrintMe mb-3"
      :class="{
        floatingSentryButton: true,
        SentryErrorGlow: sentryGlow,
      }"
      style="z-index: 10000"
      @click.stop.prevent
    >
      <i style="color: black" class="fas fa-bug" />
    </LISAButton>
    <div id="LightBoxPortal" />
    <Toast position="bottom-right" />
    <ConfirmDialog
      :pt="{
        pcRejectButton: {
          root: {
            'data-testid': 'pcRejectButton',
          },
        },
        pcAcceptButton: {
          root: {
            'data-testid': 'pcAcceptButton',
          },
        },
      }"
    />
    <NewCommandMenu v-if="useNewGlobalSearch" v-model="showNewGlobalSearch" />
    <CommandVue v-model="showLegacyCommandMenu" :enable-hotkey="!useNewGlobalSearch" />
    <LisaControl />
    <DataTestIdBadge v-if="isTest" :interval-ms="500" :only-visible="true" :offset-y="-14" />
    <VueComponentNameBadge v-if="isDev" />
  </div>
</template>

<script lang="ts">
import { useStore } from "vuex";
import { DefaultApolloClient } from "@vue/apollo-composable";
import {
  defineComponent,
  provide,
  onMounted,
  ref,
  computed,
  type WritableComputedRef,
  type InjectionKey,
  watch,
  onUnmounted,
} from "vue";
import { useRoute } from "vue-router";
import Toast, { type ToastMessageOptions } from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { useTranslation } from "@/language/translation";
// eslint-disable-next-line no-restricted-imports
import { useConfirm } from "primevue/useconfirm";
import ConfirmDialog from "primevue/confirmdialog";
import SidebarComponent from "@/components/menu/SidebarComponent.vue";
import NewSidebarComponent from "@/components/menu/NewSidebarComponent.vue";
import HeadNavbar from "@/components/HeadNavbar.vue";
import Placeholder from "@/components/shared/PlaceholderComponent.vue";
import lisaBG from "@/assets/logos/lisa.png";
import emmaBG from "@/assets/logos/lisa.png";
import smoothScroll from "@/functions/smoothScroll";
import { apolloClient } from "./graphql/apollo";
import RFIDScanComponent from "./components/rfid/RFIDScanComponent.vue";
import CommandVue from "./modules/commandMenu/views/CommandMenu.vue";
import NewCommandMenu from "./modules/commandMenu/views/NewCommandMenu.vue";
import isModifierSet from "./functions/userModifier";
import { ModifierType } from "./functions/userModifierEnum";
import { setInitialScreenshotContainer } from "./functions/ScreenshotHelper";
import LISAThemeProvider from "./modules/theme/components/LISAThemeProvider.vue";
import { useSentryFeedback } from "./modules/sentry/sentryFeedback";
import LISAButton from "./components/shared/primevue/LISAButton.vue";
import Tooltip from "primevue/tooltip";
import KeyCloakUserDialog from "./modules/keycloak/components/KeyCloakUserDialog.vue";
import LisaControl from "./components/LisaControl.vue";
import DataTestIdBadge from "./modules/devTools/components/DataTestIdBadge.vue";
import VueComponentNameBadge from "./modules/devTools/components/VueComponentNameBadge.vue";
import { ENV } from "./ENV";
import { confirmModal } from "./components/shared/form/ConfirmationModal";
import { captureException } from "@sentry/browser";
import { useDynamicTranslationCatalogSync } from "@/language/useDynamicTranslationCatalogSync";
import { isIntern, isProduction } from "./functions/environment.ts";
export const MonitorViewInjectionKey = Symbol("MonitorViewInjectionKey") as InjectionKey<
  WritableComputedRef<boolean>
>;

export default defineComponent({
  name: "App",
  components: {
    ConfirmDialog,
    HeadNavbar,
    SidebarComponent,
    NewSidebarComponent,
    Placeholder,
    RFIDScanComponent,
    Toast,
    CommandVue,
    NewCommandMenu,
    LISAThemeProvider,
    LISAButton,
    KeyCloakUserDialog,
    LisaControl,
    DataTestIdBadge,
    VueComponentNameBadge,
  },
  directives: {
    Tooltip,
  },
  provide() {
    return {
      lisaLogo: () => (ENV.VITE_APP_BRAND_NAME === "EMMA" ? emmaBG : lisaBG),
      smoothScroll: (top: number | undefined) => smoothScroll(top),
      disableRFID: () => {
        (this.$refs?.rfidComponent as any)?.disableRFID?.();
      },
      enableRFID: () => {
        (this.$refs?.rfidComponent as any)?.enableRFID?.();
      },
    };
  },
  setup() {
    const showLegacyCommandMenu = ref(false);
    const showNewGlobalSearch = ref(false);
    const monitorViewRef = ref(false);
    const spinnerVisible = ref(false);
    const route = useRoute();
    const store = useStore();
    const toast = useToast();
    const isTest = computed(() => import.meta.env.MODE === "test");
    const isDev = import.meta.env.DEV;
    window.$toast = (message: ToastMessageOptions) => {
      /**
       * providing toast to be usable in apollo and api,
       * setting defaults.
       */
      return toast.add({
        life: 3000,
        ...message,
      });
    };
    const translation = useTranslation();

    window.$confirm = useConfirm();

    const isMonitorViewEnabled = computed({
      get(): boolean {
        return (
          (route.meta?.hideNavbar as boolean) ||
          (((route.query.monitorView != null && route.query.monitorView === "true") ||
            monitorViewRef.value) &&
            (route.meta?.supportsMonitorView as boolean))
        );
      },
      set(value) {
        monitorViewRef.value = value;
      },
    });
    provide(MonitorViewInjectionKey, isMonitorViewEnabled);

    const useBackgroundImage = computed((): any => {
      return route.meta?.backgroundImage;
    });

    provide(DefaultApolloClient, apolloClient);
    useDynamicTranslationCatalogSync();

    const showSidebar = ref(true);

    const appDiv = ref<HTMLDivElement>();

    setInitialScreenshotContainer(
      computed(() => {
        return appDiv.value!;
      }),
    );

    // SENTRY
    const { attachToHTMLElement, floatingButton } = useSentryFeedback(".SentryFeedbackButton");
    const sentryGlow = computed(() => floatingButton.isGlowing.value);
    watch(
      translation.locale,
      () => {
        attachToHTMLElement();
      },
      {
        immediate: true,
      },
    );
    onMounted(async () => {
      store.dispatch("clientCache/clearEventExportPromise");
    });

    const isDarkModeEnabled = computed(() => {
      return store.state.login.user?.darkMode ?? false;
    });
    const useNewGlobalSearch = computed(() => isModifierSet(ModifierType.LISA3));
    const useNewSidebar = computed(() => isModifierSet(ModifierType.LISA3));

    const { t } = useTranslation();

    async function checkVersion() {
      if (import.meta.env.MODE !== "production") {
        return;
      }
      try {
        const res = await fetch("/version", { cache: "no-store" });
        if (!res.ok) {
          throw new Error(`Version check failed with status ${res.status}`);
        }

        const data = await res.json();

        if (data.currentVersion !== import.meta.env.__VERSION__.trim()) {
          confirmModal({
            header:
              t("global.newVersionAvailableHeader", { version: data.currentVersion }) ||
              `New Update (${data.currentVersion})`,
            message:
              t("global.newVersionAvailable") ||
              `A new version is available. Please refresh the page to access all features.
If you have any unsaved changes, please save or copy them before refreshing the page.`,
            acceptLabel: t("global.reload") || `Refresh`,
            rejectLabel: t("global.reloadLater") || `Reload later`,
            accept: () => {
              window.location.reload();
            },
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Version check failed", e);
      }
    }

    function preloadListenerFunction(
      event: Event & {
        payload: Error;
      },
    ) {
      event.preventDefault();

      const key = "vite-preload-reload";
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        if (isProduction() && !isIntern()) {
          captureException("Here we would do a hard reload!");
        } else {
          window.location.reload();
        }
      } else {
        captureException("Chunk reload failed after refresh");
        // eslint-disable-next-line no-console
        console.error("Chunk reload failed after refresh", event.payload);
      }
    }

    let intervalId: ReturnType<typeof setInterval> | undefined;

    onMounted(() => {
      checkVersion();
      intervalId = setInterval(checkVersion, 60_000 * 5);
      window.addEventListener("vite:preloadError", preloadListenerFunction);
    });

    onUnmounted(() => {
      if (intervalId != null) {
        clearInterval(intervalId);
      }
      window.removeEventListener("vite:preloadError", preloadListenerFunction);
    });

    return {
      t,
      ENV,
      sentryGlow,
      appDiv,
      isDarkModeEnabled,
      spinnerVisible,
      isMonitorViewEnabled,
      useBackgroundImage,
      showSidebar,
      showLegacyCommandMenu,
      showNewGlobalSearch,
      isTest,
      isDev,
      useNewGlobalSearch,
      useNewSidebar,
    };
  },
});
</script>

<style scoped>
.isEmma {
  background-image: url("@/assets/background_emma.png");
}
@keyframes errorGlow {
  0% {
    box-shadow: 0 0 0px var(--p-danger-color);
    border-color: var(--p-danger-color);
  }
  50% {
    box-shadow: 0 0 5px 3px var(--p-danger-color);
  }
  100% {
    box-shadow: 0 0 0px var(--p-danger-color);
  }
}

.SentryErrorGlow {
  animation: errorGlow 1s ease-in-out normal;
}

.rotationTextWrapper {
  line-height: 30px;
  height: 2em;
  overflow: hidden;
  position: absolute;
  text-align: center;
}

.textAnimation {
  animation-name: head;
  animation-duration: 30s;
  animation-iteration-count: infinite;
}

@keyframes head {
  0% {
    transform: translateY(0);
  }

  19% {
    transform: translateY(0);
  }

  20% {
    transform: translateY(-30px);
  }

  29% {
    transform: translateY(-30px);
  }

  30% {
    transform: translateY(-60px);
  }

  39% {
    transform: translateY(-60px);
  }

  40% {
    transform: translateY(-90px);
  }

  49% {
    transform: translateY(-90px);
  }

  50% {
    transform: translateY(-120px);
  }

  59% {
    transform: translateY(-120px);
  }

  60% {
    transform: translateY(-150px);
  }

  69% {
    transform: translateY(-150px);
  }

  70% {
    transform: translateY(-180px);
  }

  79% {
    transform: translateY(-180px);
  }

  80% {
    transform: translateY(-210px);
  }

  89% {
    transform: translateY(-210px);
  }

  90% {
    transform: translateY(-240px);
  }

  99% {
    transform: translateY(-240px);
  }

  100% {
    transform: translateY(-270px);
  }
}
</style>

<style lang="scss">
body {
  margin: 0;
}

#app {
  height: 100%;
}
</style>
<style>
@media (prefers-reduced-motion: reduce) {
  .p-component,
  .p-dialog,
  .p-overlay,
  .p-dropdown-panel {
    animation-duration: 0s !important;
    transition-duration: 0s !important;
  }
}

.floatingSentryButton {
  position: fixed;
  bottom: 2.2rem;
  left: 0.5rem;
  z-index: 10000;
  background-color: #f2f2f2 !important;
  border: 1px solid transparent;
}

/* BUTTONS STYLING GLOBAL */
.btn.disabled,
.btn:disabled {
  opacity: 0.5;
  color: #fff;
  background-color: #6c757d;
  border-color: #6c757d;
}
</style>
<style>
.table.b-table thead tr th {
  background-color: var(--p-primary-contrast-color) !important;
  color: white !important;
}

.b-table .table-b-table-default {
  border: 0;
}

.b-table-sticky-header {
  margin-bottom: 0 !important;
}
</style>

<style lang="scss" scoped>
#app {
  height: 100%;
  display: grid;
  grid-template-columns: 100%;
}

.main {
  display: flex;
  width: 100vw;

  .rightContainer {
    position: relative;
    flex-grow: 1;
    overflow-y: hidden;
    overflow-x: hidden;
    > * {
      overflow-y: auto;
    }
  }
}

@media print {
  body {
    height: auto !important;

    div {
      height: auto !important;
      grid-template-rows: 0px minmax(0px, auto) 0px !important;

      .main {
        height: auto !important;

        .rightContainer {
          overflow-x: visible !important;
          padding-top: 0 !important;
        }
      }
    }
  }
}
</style>
